/*
Toggle class on another element.

HTML Usage:
<toggle-element-class data-options='{}'>
  Toggle element class button
</toggle-element-class>
*/

if (!customElements.get('toggle-element-class')) {
  class ToggleElementClass extends HTMLElement {
    constructor() {
      super();

      // Options
      this.options = {
        toggleClass: 'toggle--is-active',
        toggleTriggerClass: '',
        target: 'body',
        onHover: false,
        hoverDelay: 0,
        preventDefault: true,
        stopPropagation: true,
        ariaExpanded: false,
        closeOnTargetClick: false, // Boolean - allow close when target is clicked
        closeOnScrollDown: false, // String - class name expected on scrolldown object
      };

      // Get options from element data and combine with this.options
      if (this?.dataset?.options) {
        const dataOptions = parseJson(this.dataset.options);
        this.options = {
          ...this.options,
          ...dataOptions,
        };
      }

      // Reduce actions (So the event can also be removed)
      this.reducer = {
        click: (e) => this.debounceClickEvent(e),
        mouseEnter: (e) => this.debouncedOnMouse(e, 'add'),
        mouseLeave: (e) => this.debouncedOnMouse(e, 'remove'),
        touch: (e) => this.debouncedOnMouse(e, 'touch'),
      };

      // Construct webcomponent
      this.construct();
    }

    /*
      Construct webcomponent
    */
    construct() {
      this.target = document.querySelector(this.options.target);

      if (!this.target) {
        return false;
      }

      // Click
      this.removeEventListener('click', this.reducer.click);
      this.addEventListener('click', this.reducer.click);

      // Mouse enter and leave
      if (this.options.onHover) {
        this.removeEventListener('mouseenter', this.reducer.mouseEnter);
        this.addEventListener('mouseleave', this.reducer.mouseLeave);
      }

      if (this.options.closeOnTargetClick) {
        this.target.removeEventListener('click', this.remove.bind(this));
        this.target.addEventListener('click', this.remove.bind(this));
      }

      if (this.options.closeOnScrollDown) {
        this.touchstart = 0;
        window.removeEventListener('touchstart', this.touch.bind(this));
        window.removeEventListener('touchend', this.touch.bind(this));
        window.addEventListener('touchstart', this.touch.bind(this));
        window.addEventListener('touchend', this.touch.bind(this));
      }
    }

    /*
      Debounce click event
      @param e {object}: mouseevent
      Called by add event listener -> reducer.
    */
    debounceClickEvent(e) {
      if (!e) {
        return false;
      }
      this.preventDefault(e);
      this.stopPropagation(e);
      this.toggle();
    }

    /*
      Debounced mouse event
      @param e {object}: mouseevent
      @param type {string}: open/close/closeAll
      Called by add event listener -> reducer.
    */
    debouncedOnMouse(e, type) {
      if (!e) {
        return false;
      }
      const debouncer = debounce((type) => {
        switch (type) {
          case 'add':
            this.add();
            break;
          case 'remove':
            this.remove();
            break;
          case 'touch':
            this.touch();
            break;
          default:
            this.toggle();
            break;
        }
      }, this.options.hoverDelay);
      debouncer(type);
    }

    // @param e {object}: click event
    preventDefault(e) {
      if (this.options.preventDefault) {
        e.preventDefault();
      }
    }

    // @param e {object}: click event
    stopPropagation(e) {
      if (this.options.stopPropagation) {
        e.stopPropagation();
      }
    }

    add() {
      const { toggleClass, toggleTriggerClass } = this.options;

      if (this.target) {
        const list = this.createToggleClassList(toggleClass);
        this.target.classList.add(...list.filter(Boolean));
      }
      if (toggleTriggerClass) {
        this.classList.add(toggleTriggerClass);
      }
      // Switch aria-expanded
      if (this.options.ariaExpanded) {
        this.setAttribute('aria-expanded', true);
      }
    }

    remove() {
      const { toggleClass, toggleTriggerClass } = this.options;

      if (this.target) {
        const list = this.createToggleClassList(toggleClass);
        this.target.classList.remove(...list.filter(Boolean));
      }
      if (toggleTriggerClass) {
        this.classList.remove(toggleTriggerClass);
      }
      // Switch aria-expanded
      if (this.options.ariaExpanded) {
        this.setAttribute('aria-expanded', false);
      }
    }

    createToggleClassList(toggleClass) {
      return toggleClass instanceof Array ? toggleClass : [toggleClass];
    }

    touch(e) {
      const { toggleClass, closeOnScrollDown } = this.options;
      // Ignore touch event when the popup is closed
      if (!this.target.classList.contains(toggleClass)) {
        return;
      } else {
        // Save touchstart position to calculate touch down behavior
        if (e.type === 'touchstart' && e.touches.length > 0) {
          const touchedTarget = e.touches[0];
          const { clientY, target } = touchedTarget;
          if (target.classList.contains(closeOnScrollDown)) {
            this.touchstart = clientY;
          }
        }
        // Compare touchstart and touchend Y position and toggle class if needed
        else if (e.type === 'touchend' && e.changedTouches.length > 0 && this.touchstart > 0) {
          const touchedTarget = e.changedTouches[0];
          const { clientY } = touchedTarget;
          const touchend = clientY;
          const touchDiff = this.touchstart - touchend;
          // Reset touchstart
          this.touchstart = 0;

          // Toggle only when movement is bigger than 30px downwards
          if (touchDiff < -20) {
            this.toggle();
          }
        }
      }
    }

    toggle() {
      const { toggleClass } = this.options;
      const list = this.createToggleClassList(toggleClass);
      if (!this.target) {
        return false;
      }
      if (!Array.from(this.target.classList).some((e) => list.includes(e))) {
        this.add();
      } else {
        this.remove();
      }
    }
  }

  window.ToggleElementClass = ToggleElementClass;

  customElements.define('toggle-element-class', ToggleElementClass);
}
